<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter filter-title="Assets">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.serialNumber"
                label="Serial Number"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :params="{
                  includeEmployees: 'currentAndPast',
                }"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <asset-category-autocomplete
                v-model="filters.assetCategory"
                :rules="rules.assetCategory"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <asset-brand-autocomplete
                v-model="filters.assetBrand"
                :rules="rules.assetBrand"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <asset-vendor-autocomplete
                v-model="filters.assetVendor"
                :rules="rules.assetVendor"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            type="reset"
            display-type="ghost"
            :label="$t('general.reset')"
          />
          <submit-button :label="$t('general.search')" />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-button
          icon-name="plus"
          display-type="secondary"
          :label="$t('general.add')"
          @click="onClickAdd"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :loading="isLoading"
        :total="total"
        :show-divider="$can.create(`time_projects`)"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
          :selectable="$can.delete(`time_projects`)"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {validSelection} from '@/core/util/validation/rules';
import useSort from '@ohrm/core/util/composable/useSort';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import AssetCategoryAutocomplete from '@/orangehrmAdminPlugin/components/AssetCategoryAutocomplete.vue';
import AssetBrandAutocomplete from '@/orangehrmAdminPlugin/components/AssetBrandAutocomplete.vue';
import AssetVendorAutocomplete from '@/orangehrmAdminPlugin/components/AssetVendorAutocomplete.vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';

const defaultFilters = {
  employee: null,
  assetCategory: null,
  assetBrand: null,
  assetVendor: null,
  serialNumber: null,
};

const defaultSortOrder = {
  'project.name': 'ASC',
  'customer.name': 'DEFAULT',
  'projectCategory.name': 'DEFAULT',
  'employee.lastName': 'DEFAULT',
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'asset-category-autocomplete': AssetCategoryAutocomplete,
    'asset-brand-autocomplete': AssetBrandAutocomplete,
    'asset-vendor-autocomplete': AssetVendorAutocomplete,
    'delete-confirmation': DeleteConfirmationDialog,
  },
  props: {
    unselectableIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const {$tEmpName} = useEmployeeNameTranslate();
    const projectNormalizer = (data) => {
      return data.map((item) => {
        const selectable = props.unselectableIds.findIndex(
          (id) => id == item.id,
        );
        return {
          id: item.id,
          category: item.assetCategory?.name,
          serialNumber: item.serialNumber,
          brand: item.assetBrand?.name,
          model: item.model,
          description: item.description,
          warrantyStart: item.warrantyStart,
          warrantyEnd: item.warrantyEnd,
          acquired: item.acquired,
          employee: item.employee?.empNumber
            ? $tEmpName(item.employee, {includeMiddle: false})
            : '',
          assignedFrom: item.assignedFrom,
          status: item.status,
          isSelectable: selectable === -1,
        };
      });
    };

    const filters = ref({...defaultFilters});
    const {sortDefinition, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });

    const serializedFilters = computed(() => {
      return {
        assetCategory: filters.value.assetCategory?.id,
        assetBrand: filters.value.assetBrand?.id,
        assetVendor: filters.value.assetVendor?.id,
        empNumber: filters.value.employee?.id,
        serialNumber: filters.value?.serialNumber,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/assets',
    );

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: projectNormalizer,
    });

    onSort(execQuery);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      sortDefinition,
    };
  },
  data() {
    return {
      checkedItems: [],
      rules: {
        assetCategory: [validSelection],
      },
    };
  },
  computed: {
    headers() {
      const headers = [
        {
          name: 'category',
          title: 'Category',
          style: {flex: '10%'},
        },
        {
          name: 'serialNumber',
          title: 'Serial Number',
          style: {flex: '10%'},
        },
        {
          name: 'brand',
          title: 'Brand',
          style: {flex: '10%'},
        },
        {
          name: 'model',
          title: 'Model',
          style: {flex: '10%'},
        },
        {
          name: 'status',
          title: 'Status',
          style: {flex: '10%'},
        },
        {
          name: 'employee',
          title: 'Assigned Employee',
          style: {flex: '10%'},
        },
        {
          name: 'assignedFrom',
          title: 'Assigned On',
          style: {flex: '10%'},
        },
        {
          name: 'warrantyEnd',
          title: 'Warranty End',
          style: {flex: '10%'},
        },
      ];
      const headerActions = {
        name: 'actions',
        slot: 'action',
        title: this.$t('general.actions'),
        style: {flex: '10%'},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {},
      };
      headerActions.cellConfig.delete = {
        onClick: this.onClickDelete,
        props: {
          name: 'trash',
        },
      };
      headerActions.cellConfig.edit = {
        onClick: this.onClickEdit,
        props: {
          name: 'pencil-fill',
        },
      };
      if (Object.keys(headerActions.cellConfig).length > 0) {
        headers.push(headerActions);
      }
      return headers;
    },
  },
  methods: {
    onClickAdd() {
      navigate('/admin/saveAsset');
    },
    onClickEdit(item) {
      navigate('/admin/saveAsset/{id}', {id: item.id});
    },
    onClickDelete(item) {
      const isSelectable = this.unselectableIds.findIndex(
        (id) => id == item.id,
      );
      if (isSelectable > -1) {
        return this.$toast.error({
          title: this.$t('general.error'),
          message: 'Deletion not allowed.',
        });
      }
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteData([item.id]);
        }
      });
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteData(ids);
        }
      });
    },
    async deleteData(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
